"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.doMultiplication = exports.doDivision = exports.doSubtraction = exports.doAddition = exports.parseQuantity = exports.Quantity = void 0;
var math_1 = require("../util/math");
var units_1 = require("../util/units");
var Quantity = /** @class */ (function () {
    function Quantity(value, unit) {
        this.value = value;
        this.unit = unit;
        if (this.value == null || isNaN(this.value)) {
            throw new Error('Cannot create a quantity with an undefined value');
        }
        else if (!(0, math_1.isValidDecimal)(this.value)) {
            throw new Error('Cannot create a quantity with an invalid decimal value');
        }
        // Attempt to parse the unit with UCUM. If it fails, throw a friendly error.
        if (this.unit != null) {
            var validation = (0, units_1.checkUnit)(this.unit);
            if (!validation.valid) {
                throw new Error(validation.message);
            }
        }
    }
    Object.defineProperty(Quantity.prototype, "isQuantity", {
        get: function () {
            return true;
        },
        enumerable: false,
        configurable: true
    });
    Quantity.prototype.clone = function () {
        return new Quantity(this.value, this.unit);
    };
    Quantity.prototype.toString = function () {
        return "".concat(this.value, " '").concat(this.unit, "'");
    };
    Quantity.prototype.sameOrBefore = function (other) {
        if (other != null && other.isQuantity) {
            var otherVal = (0, units_1.convertUnit)(other.value, other.unit, this.unit);
            if (otherVal == null) {
                return null;
            }
            else {
                return this.value <= otherVal;
            }
        }
    };
    Quantity.prototype.sameOrAfter = function (other) {
        if (other != null && other.isQuantity) {
            var otherVal = (0, units_1.convertUnit)(other.value, other.unit, this.unit);
            if (otherVal == null) {
                return null;
            }
            else {
                return this.value >= otherVal;
            }
        }
    };
    Quantity.prototype.after = function (other) {
        if (other != null && other.isQuantity) {
            var otherVal = (0, units_1.convertUnit)(other.value, other.unit, this.unit);
            if (otherVal == null) {
                return null;
            }
            else {
                return this.value > otherVal;
            }
        }
    };
    Quantity.prototype.before = function (other) {
        if (other != null && other.isQuantity) {
            var otherVal = (0, units_1.convertUnit)(other.value, other.unit, this.unit);
            if (otherVal == null) {
                return null;
            }
            else {
                return this.value < otherVal;
            }
        }
    };
    Quantity.prototype.equals = function (other) {
        if (other != null && other.isQuantity) {
            if ((!this.unit && other.unit) || (this.unit && !other.unit)) {
                return false;
            }
            else if (!this.unit && !other.unit) {
                return this.value === other.value;
            }
            else {
                var otherVal = (0, units_1.convertUnit)(other.value, other.unit, this.unit);
                if (otherVal == null) {
                    return null;
                }
                else {
                    return (0, math_1.decimalAdjust)('round', this.value, -8) === otherVal;
                }
            }
        }
    };
    Quantity.prototype.convertUnit = function (toUnit) {
        var value = (0, units_1.convertUnit)(this.value, this.unit, toUnit);
        // Need to pass through constructor again to catch invalid units
        return new Quantity(value, toUnit);
    };
    Quantity.prototype.dividedBy = function (other) {
        if (other == null || other === 0 || other.value === 0) {
            return null;
        }
        else if (!other.isQuantity) {
            // convert it to a quantity w/ unit 1
            other = new Quantity(other, '1');
        }
        var _a = (0, units_1.normalizeUnitsWhenPossible)(this.value, this.unit, other.value, other.unit), val1 = _a[0], unit1 = _a[1], val2 = _a[2], unit2 = _a[3];
        var resultValue = val1 / val2;
        var resultUnit = (0, units_1.getQuotientOfUnits)(unit1, unit2);
        // Check for invalid unit or value
        if (resultUnit == null || (0, math_1.overflowsOrUnderflows)(resultValue)) {
            return null;
        }
        return new Quantity((0, math_1.decimalAdjust)('round', resultValue, -8), resultUnit);
    };
    Quantity.prototype.multiplyBy = function (other) {
        if (other == null) {
            return null;
        }
        else if (!other.isQuantity) {
            // convert it to a quantity w/ unit 1
            other = new Quantity(other, '1');
        }
        var _a = (0, units_1.normalizeUnitsWhenPossible)(this.value, this.unit, other.value, other.unit), val1 = _a[0], unit1 = _a[1], val2 = _a[2], unit2 = _a[3];
        var resultValue = val1 * val2;
        var resultUnit = (0, units_1.getProductOfUnits)(unit1, unit2);
        // Check for invalid unit or value
        if (resultUnit == null || (0, math_1.overflowsOrUnderflows)(resultValue)) {
            return null;
        }
        return new Quantity((0, math_1.decimalAdjust)('round', resultValue, -8), resultUnit);
    };
    return Quantity;
}());
exports.Quantity = Quantity;
function parseQuantity(str) {
    var components = /([+|-]?\d+\.?\d*)\s*('(.+)')?/.exec(str);
    if (components != null && components[1] != null) {
        var value = parseFloat(components[1]);
        if (!(0, math_1.isValidDecimal)(value)) {
            return null;
        }
        var unit = void 0;
        if (components[3] != null) {
            unit = components[3].trim();
        }
        else {
            unit = '';
        }
        return new Quantity(value, unit);
    }
    else {
        return null;
    }
}
exports.parseQuantity = parseQuantity;
function doScaledAddition(a, b, scaleForB) {
    if (a != null && a.isQuantity && b != null && b.isQuantity) {
        var _a = (0, units_1.normalizeUnitsWhenPossible)(a.value, a.unit, b.value * scaleForB, b.unit), val1 = _a[0], unit1 = _a[1], val2 = _a[2], unit2 = _a[3];
        if (unit1 !== unit2) {
            // not compatible units, so we can't do addition
            return null;
        }
        var sum = val1 + val2;
        if ((0, math_1.overflowsOrUnderflows)(sum)) {
            return null;
        }
        return new Quantity(sum, unit1);
    }
    else if (a.copy && a.add) {
        // Date / DateTime require a CQL time unit
        var cqlUnitB = (0, units_1.convertToCQLDateUnit)(b.unit) || b.unit;
        return a.copy().add(b.value * scaleForB, cqlUnitB);
    }
    else {
        throw new Error('Unsupported argument types.');
    }
}
function doAddition(a, b) {
    return doScaledAddition(a, b, 1);
}
exports.doAddition = doAddition;
function doSubtraction(a, b) {
    return doScaledAddition(a, b, -1);
}
exports.doSubtraction = doSubtraction;
function doDivision(a, b) {
    if (a != null && a.isQuantity) {
        return a.dividedBy(b);
    }
}
exports.doDivision = doDivision;
function doMultiplication(a, b) {
    if (a != null && a.isQuantity) {
        return a.multiplyBy(b);
    }
    else {
        return b.multiplyBy(a);
    }
}
exports.doMultiplication = doMultiplication;
//# sourceMappingURL=quantity.js.map